"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _hopsSdkJs = require("@hps/hops-sdk-js");
var _styles = require("@mui/styles");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _SeatReservationSpaceModule = _interopRequireDefault(require("./SeatReservationSpace.module.scss"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * A single "space" in the seat reservation UI
 *
 * A `SeatPlanSpace` within a `SeatPlan`.
 *
 * @package HOPS
 * @subpackage SeatReservation
 * @copyright Heritage Operations Processing Limited
 */const SeatReservationSpace = props => {
  /**
   * Key handler.
   *
   * @param {Event} e
   * @return {void}
   */
  const handleKey = e => {
    if (e.which === 13 || e.which === 32) {
      e.preventDefault();
      handleSelect();
    }
  };

  /**
   * Selection toggled.
   *
   * @return {void}
   */
  const handleSelect = () => {
    if (selectable) {
      props.onToggleSelection(props.space);
    }
  };

  /**
   * Get the styles to apply to a space's outer element.
   *
   * @param {Object} space
   * @return {Object}
   */
  const getSpaceOuterStyles = space => {
    return {
      "gridColumn": `${space.X + 1}`,
      "gridRow": `${space.Y + 1}`,
      "--hops-seat-reservation-space-border-colour": props.theme.palette.text.disabled,
      "--hops-seat-reservation-bulkhead-border-colour": props.theme.palette.text.primary
    };
  };
  const space = props.space;

  /**
   * Get whether this space is selected locally (by our user).
   * 
   * @return {Boolean}
   */
  const selected = !!props.selected;

  /**
   * Get whether this space is booked by somebody else (on the server).
   *
   * We ignore the booked status if the space is selected; as selection 
   * is disabled on booked spaces, this should mean the user has returned 
   * to a session having previously added the space to their basket, so 
   * the seat data marks it as booked, but it is booked *for them* via 
   * their selection.
   */
  const booked = !!props.booked;

  /**
   * Get wether this space is booked, within the same order as the seating plan is showing.
   */
  const bookedWithinOrder = !!props.bookedWithinOrder;

  /**
   * Get whether this space is currently unavailable because it's in an exclusive container.
   * (Exclusive containers can only contain seat reservations from the same order/basket)
   * 
   * This is just visual information, the availability is also enforced in the claim
   * handler, in case a seat in the container is booked while the seating picker
   * is open client side.
   */
  const excluded = !!props.excluded || props.space.Bookable === false;

  /**
   * Get whether this space is currently selectable.
   */
  const selectable = !booked && !excluded && props.space.Bookable && (props.selectable || selected);

  /**
   * Class names to apply to the inner space element
   */
  const innerClasses = [_SeatReservationSpaceModule.default.root, _SeatReservationSpaceModule.default.space, _SeatReservationSpaceModule.default[`shape_${space.Shape}`], space.Shape === _hopsSdkJs.SeatPlanSpaceShapes.Divider && [90, 270].includes(Math.abs(space.Orientation)) ? _SeatReservationSpaceModule.default.horizontal : undefined, booked && _SeatReservationSpaceModule.default.booked, bookedWithinOrder && _SeatReservationSpaceModule.default.bookedWithinOrder, excluded && _SeatReservationSpaceModule.default.excluded, selectable ? _SeatReservationSpaceModule.default.selectable : _SeatReservationSpaceModule.default.unselectable, selected && _SeatReservationSpaceModule.default.selected].filter(c => c);

  /**
   * Class names to apply to the outer space element
   */
  const outerClasses = [_SeatReservationSpaceModule.default.root, _SeatReservationSpaceModule.default[`shape_${space.Shape}`], space.Bulkhead0 && _SeatReservationSpaceModule.default.bulkhead0, space.Bulkhead90 && _SeatReservationSpaceModule.default.bulkhead90, space.Bulkhead180 && _SeatReservationSpaceModule.default.bulkhead180, space.Bulkhead270 && _SeatReservationSpaceModule.default.bulkhead270].filter(c => c);

  /**
   * Style object to apply to the inner space element
   */
  const innerStyles = {
    /** Use the space's colour as its background when available */
    background: space.Colour || undefined,
    /** Apply the space's orientation to correctly rotate the shape */
    transform: space.Shape !== _hopsSdkJs.SeatPlanSpaceShapes.Divider ? `rotate(${space.Orientation}deg)` : undefined
  };

  /**
   * Style object to apply to the inner space label element
   */
  const labelStyles = {
    /** Cancel out the space's orientation to correctly rotate the label */
    transform: space.Shape !== _hopsSdkJs.SeatPlanSpaceShapes.Divider ? `rotate(${360 - space.Orientation}deg)` : undefined
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    className: outerClasses.join(" "),
    style: getSpaceOuterStyles(space),
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      className: innerClasses.join(" "),
      onClick: selectable ? handleSelect : undefined,
      onKeyDown: handleKey,
      style: innerStyles,
      tabIndex: selectable ? "0" : undefined,
      title: `${space.Label || ""}${booked ? " (Booked)" : ""}${selected ? " (Selected)" : ""}`,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: labelStyles,
        children: space.Label
      })
    })
  });
};
SeatReservationSpace.propTypes = {
  isInContainer: _propTypes.default.bool,
  onToggleSelection: _propTypes.default.func,
  selected: _propTypes.default.bool,
  selectable: _propTypes.default.bool,
  space: _propTypes.default.object
};
SeatReservationSpace.defaultProps = {
  isInContainer: false,
  selected: false,
  selectable: true
};
var _default = exports.default = (0, _styles.withTheme)(SeatReservationSpace);